import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import {
  DashboardRounded,
  EventAvailableRounded,
  SelfImprovementRounded,
  CollectionsRounded,
  MenuBookRounded,
  CampaignRounded,
  FormatQuoteRounded,
  PermMediaRounded,
  HistoryRounded,
  PeopleAltRounded,
  SavingsRounded,
  TempleBuddhist,
  AssignmentTurnedInRounded,
  SupervisorAccountRounded,
  HubRounded,
  PersonSearchRounded,
  GroupsRounded,
  InsightsRounded,
  SecurityRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  KeyboardArrowDownRounded,
  AssignmentRounded,
  EditRounded,
} from '@mui/icons-material';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

import ColorSchemeToggle from './ColorSchemeToggle';
import { closeSidebar } from '../../utils/sidebar';
import { useUserStore } from '../../store/useUserStore';
import { isProfileComplete } from '../../utils/user';
import { fetchCurrentUser } from '../../api';

type NavItem = {
  label: string;
  icon: React.ElementType;
  path?: string;
  badge?: string;
  children?: NavItem[];
  group?: string;
};

const NAV_LINKS: NavItem[] = [
  { label: 'Dashboard', icon: DashboardRounded, path: '/', group: 'main' },
  { label: 'Users', icon: PeopleAltRounded, path: '/users', group: 'main' },
  { label: 'User Bulk Edit', icon: EditRounded, path: '/users/bulk-edit', group: 'main' },
  { label: 'Donations', icon: SavingsRounded, path: '/donations', group: 'main' },
  {
    label: 'Congregation',
    icon: TempleBuddhist,
    path: '/congregation/dashboard',
    group: 'congregation',
    children: [
      { label: 'Dashboard', icon: DashboardRounded, path: '/congregation/dashboard' },
      { label: 'Applications', icon: AssignmentTurnedInRounded, path: '/congregation/applications' },
      { label: 'Mentor Staffing', icon: SupervisorAccountRounded, path: '/congregation/mentors' },
      { label: 'Masters', icon: HubRounded, path: '/congregation/masters' },
      { label: 'Batches', icon: GroupsRounded, path: '/congregation/batches' },
      { label: 'Devotees', icon: PersonSearchRounded, path: '/congregation/devotees' },
      { label: 'Sadhana', icon: SelfImprovementRounded, path: '/congregation/sadhana' },
      { label: 'Books', icon: MenuBookRounded, path: '/congregation/books' },
      { label: 'Attendance', icon: EventAvailableRounded, path: '/congregation/attendance' },
      { label: 'Services', icon: AssignmentRounded, path: '/congregation/services' },
      { label: 'Service Types', icon: HubRounded, path: '/congregation/service-types' },
    ],
  },
  { label: 'Events', icon: EventAvailableRounded, path: '/events', group: 'main' },
  { label: 'Darshan', icon: SelfImprovementRounded, path: '/darshan', group: 'main' },
  { label: 'Darshan Gallery', icon: CollectionsRounded, path: '/darshan-gallery', group: 'main' },
  { label: 'Articles', icon: MenuBookRounded, path: '/articles', group: 'main' },
  { label: 'Announcements', icon: CampaignRounded, path: '/announcements', group: 'main', badge: 'New' },
  { label: 'Quotes', icon: FormatQuoteRounded, path: '/quotes', group: 'main' },
  { label: 'Media', icon: PermMediaRounded, path: '/media', group: 'main' },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useUserStore();

  const getProfileCompleteUrl = React.useCallback(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isLocalhost ? 'http://localhost:3001' : 'https://kc.iskconkanpur.org';
    return `${baseUrl}/profile/complete`;
  }, []);

  // Expanded sections state
  const [expandedSections, setExpandedSections] = React.useState({
    main: true,
    congregation: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = await fetchCurrentUser();
        setUser(currentUser as any);
        if (!isProfileComplete(currentUser as any)) {
          window.location.href = getProfileCompleteUrl();
          return;
        }
      } catch (error) {
        console.warn('Failed to fetch current user', error);
      }
    };

    fetchUser();
  }, [location.pathname, navigate, setUser]);

  const isActivePath = React.useCallback(
    (path?: string) => {
      if (!path) return false;
      return path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
    },
    [location.pathname],
  );

  // Auto-expand congregation section if a congregation route is active
  React.useEffect(() => {
    const congregationItems = NAV_LINKS.find((item) => item.group === 'congregation');
    if (congregationItems?.children) {
      const isCongregatinActive = congregationItems.children.some((child) => isActivePath(child.path));
      if (isCongregatinActive && !expandedSections.congregation) {
        setExpandedSections((prev) => ({ ...prev, congregation: true }));
      }
    }
  }, [isActivePath, expandedSections.congregation]);

  const handleRouteChange = React.useCallback(
    (path: string) => {
      navigate(path);
      closeSidebar();
    },
    [navigate],
  );

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: 'fixed',
        transform: {
          xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
          md: 'none',
        },
        transition: 'transform 0.4s, width 0.4s',
        zIndex: 10000,
        height: '100dvh',
        maxHeight: '100dvh',
        width: 'var(--Sidebar-width)',
        top: 0,
        left: 0,
        px: 1.5,
        py: 1.25,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        borderRight: '1px solid',
        borderColor: 'divider',
        boxSizing: 'border-box',
        background: (theme) => {
          const isDark = theme.palette.mode === 'dark';
          const from = isDark ? 'rgba(16,18,24,0.94)' : 'rgba(250,252,255,0.98)';
          const to = isDark ? 'rgba(16,18,24,0.84)' : 'rgba(240,244,255,0.92)';
          return `linear-gradient(165deg, ${from} 0%, ${to} 100%)`;
        },
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--Sidebar-width': '220px',
            [theme.breakpoints.up('lg')]: {
              '--Sidebar-width': '240px',
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: 'fixed',
          zIndex: 9998,
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          height: '100dvh',
          opacity: 'var(--SideNavigation-slideIn)',
          backgroundColor: 'var(--joy-palette-background-backdrop)',
          transition: 'opacity 0.4s',
          transform: {
            xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
            lg: 'translateX(-100%)',
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Sheet
        variant="plain"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 'md',
          p: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.85,
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Box
          component="img"
          src="https://cdn.iskconkanpur.org/static/brand/isk-logo-circle.png"
          alt="ISKCON Kanpur"
          sx={{ width: 36, height: 36, flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0, flex: 1, position: 'relative', zIndex: 1 }}>
          <Typography
            level="title-sm"
            sx={{ fontWeight: 700, letterSpacing: 0.15, fontSize: '0.8125rem', color: 'text.primary', lineHeight: 1.2 }}
          >
            ISKCON Kanpur
          </Typography>
        </Box>
        <ColorSchemeToggle
          sx={{
            '--IconButton-size': '30px',
            borderRadius: 'sm',
            boxShadow: 'none',
            backgroundColor: 'background.level1',
            border: '1px solid',
            borderColor: 'neutral.outlinedBorder',
          }}
        />
      </Sheet>
      <Box
        sx={{
          minHeight: 0,
          overflow: 'hidden auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          pr: 0.5,
          pb: 0.25,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'neutral.outlinedBorder',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'neutral.outlinedHoverBorder',
            },
          },
        }}
      >
        {/* Main Navigation */}
        <Box>
          <Box
            onClick={() => toggleSection('main')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              cursor: 'pointer',
              borderRadius: 'xs',
              px: 0.75,
              py: 0.3,
              mx: -0.75,
              transition: 'backgroundColor 0.2s ease',
              '&:hover': {
                backgroundColor: 'background.level1',
              },
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                color: 'text.tertiary',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: '0.6rem',
              }}
            >
              Navigation
            </Typography>
            <KeyboardArrowDownRounded
              sx={{
                fontSize: '1rem',
                transform: expandedSections.main ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
                color: 'text.tertiary',
              }}
            />
          </Box>
          {expandedSections.main && (
            <List
              size="sm"
              sx={{
                gap: 0.35,
                '--ListItem-radius': (theme) => theme.vars.radius.md,
              }}
            >
              {NAV_LINKS.filter((item) => item.group === 'main').map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);
                return (
                  <ListItem key={item.label} sx={{ '--ListItem-gap': '8px' }}>
                    <ListItemButton
                      onClick={() => handleRouteChange(item.path!)}
                      selected={active}
                      sx={[
                        {
                          position: 'relative',
                          borderRadius: 'md',
                          px: 1.25,
                          py: 0.6,
                          gap: 0.85,
                          minHeight: '34px',
                          alignItems: 'center',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                          fontWeight: 500,
                          color: 'text.secondary',
                          backgroundColor: 'transparent',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 2.5,
                            height: '50%',
                            borderRadius: 2,
                            backgroundColor: 'transparent',
                          },
                        },
                        active
                          ? (theme) => ({
                              backgroundColor: theme.vars.palette.background.level1,
                              color: theme.vars.palette.text.primary,
                              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                              transform: 'translateX(4px)',
                              '&::before': {
                                backgroundColor: theme.vars.palette.primary.solidBg,
                              },
                            })
                          : (theme) => ({
                              color: theme.vars.palette.text.primary,
                              '&:hover': {
                                backgroundColor: theme.vars.palette.background.level1,
                                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                                transform: 'translateX(4px)',
                                '&::before': {
                                  backgroundColor: theme.vars.palette.neutral.outlinedColor,
                                },
                              },
                            }),
                      ]}
                    >
                      <ListItemDecorator sx={{ color: active ? 'inherit' : 'neutral.600', transition: 'color 0.2s' }}>
                        <Icon fontSize="small" />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm" sx={{ fontWeight: active ? 600 : 500 }}>
                          {item.label}
                        </Typography>
                      </ListItemContent>
                      {item.badge ? (
                        <Chip size="sm" variant={active ? 'solid' : 'soft'} color="primary" sx={{ fontWeight: 600, borderRadius: 'xs' }}>
                          {item.badge}
                        </Chip>
                      ) : null}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        {/* Congregation Section */}
        <Box>
          {NAV_LINKS.filter((item) => item.group === 'congregation').map((item) => {
            const Icon = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const expanded = expandedSections.congregation;
            const active = hasChildren
              ? item.children!.some((child) => isActivePath(child.path))
              : isActivePath(item.path);

            return (
              <Box key={item.label}>
                <Box
                  onClick={() => {
                    if (hasChildren) {
                      toggleSection('congregation');
                    } else if (item.path) {
                      handleRouteChange(item.path);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 0.5,
                    cursor: 'pointer',
                    borderRadius: 'xs',
                    px: 0.75,
                    py: 0.3,
                    mx: -0.75,
                    transition: 'backgroundColor 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'background.level1',
                    },
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{
                      color: 'text.tertiary',
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      fontWeight: 700,
                      fontSize: '0.6rem',
                    }}
                  >
                    {item.label}
                  </Typography>
                  {hasChildren && (
                    <KeyboardArrowDownRounded
                      sx={{
                        fontSize: '1rem',
                        transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s ease',
                        color: 'text.tertiary',
                      }}
                    />
                  )}
                </Box>

                {hasChildren && expanded && (
                  <List
                    size="sm"
                    sx={{
                      gap: 0.35,
                      '--ListItem-radius': (theme) => theme.vars.radius.sm,
                    }}
                  >
                    {item.children!.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = isActivePath(child.path);
                      return (
                        <ListItem key={child.label} sx={{ '--ListItem-gap': '8px' }}>
                          <ListItemButton
                            onClick={() => handleRouteChange(child.path!)}
                            selected={childActive}
                            sx={[
                              {
                                position: 'relative',
                                borderRadius: 'sm',
                                px: 1.25,
                                py: 0.5,
                                gap: 0.85,
                                minHeight: '30px',
                                alignItems: 'center',
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                                fontWeight: 500,
                                color: 'text.secondary',
                                backgroundColor: 'transparent',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 4,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: 2.5,
                                  height: '50%',
                                  borderRadius: 2,
                                  backgroundColor: 'transparent',
                                },
                              },
                              childActive
                                ? (theme) => ({
                                    backgroundColor: theme.vars.palette.background.level1,
                                    color: theme.vars.palette.text.primary,
                                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                                    transform: 'translateX(4px)',
                                    '&::before': {
                                      backgroundColor: theme.vars.palette.primary.solidBg,
                                    },
                                  })
                                : (theme) => ({
                                    color: theme.vars.palette.text.primary,
                                    '&:hover': {
                                      backgroundColor: theme.vars.palette.background.level1,
                                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                                      transform: 'translateX(4px)',
                                      '&::before': {
                                        backgroundColor: theme.vars.palette.neutral.outlinedColor,
                                      },
                                    },
                                  }),
                            ]}
                          >
                            <ListItemDecorator sx={{ color: childActive ? 'inherit' : 'neutral.500', transition: 'color 0.2s' }}>
                              <ChildIcon fontSize="small" />
                            </ListItemDecorator>
                            <ListItemContent>
                              <Typography level="body-sm" sx={{ fontWeight: childActive ? 600 : 500 }}>
                                {child.label}
                              </Typography>
                            </ListItemContent>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Account Card */}
      <Sheet
        variant="plain"
        sx={{
          mt: 0.35,
          pt: 0.75,
          pb: 0.65,
          px: 1.15,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.65,
          borderTop: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          background: 'transparent',
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.75,
            backgroundColor: 'background.body',
            borderRadius: 'md',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              variant="soft"
              color="primary"
              sx={{ width: 32, height: 32, fontSize: '0.875rem', fontWeight: 600 }}
              src={user?.photo_url || undefined}
            >
              {user?.legalName?.charAt(0) || 'V'}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography level="title-sm" sx={{ fontWeight: 600, fontSize: '0.8125rem' }} noWrap>
                {user?.legalName || 'Valued Devotee'}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.6875rem' }} noWrap>
                {user?.email || 'seva@example.com'}
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          endDecorator={<LogoutRoundedIcon />}
          sx={{
            width: '100%',
            borderRadius: 'md',
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
            fontWeight: 500,
            minHeight: '32px',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'background.level1',
              borderColor: 'neutral.outlinedHoverBorder',
            },
          }}
          onClick={() => {
            const redirectUrl = encodeURIComponent(window.location.href);
            window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signout?redirect=${redirectUrl}`;
          }}
        >
          Sign out
        </Button>
      </Sheet>
    </Sheet>
  );
}
